/**
 * FeedbackDialog Component
 *
 * Modal dialog shown when a user rates an iteration < 4 stars.
 * Two-step flow:
 *   Step 1: Optional feedback text
 *   Step 2: Ask if user wants to request a revision
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface FeedbackDialogProps {
  isOpen: boolean;
  stars: number;
  onSubmit: (feedback?: string, requestRevision?: boolean) => void;
  onClose: () => void;
}

export default function FeedbackDialog({
  isOpen,
  stars,
  onSubmit,
  onClose,
}: FeedbackDialogProps) {
  const { t } = useTranslation('tasks');
  const [feedback, setFeedback] = useState('');
  const [step, setStep] = useState<'feedback' | 'revision'>('feedback');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFeedback('');
      setStep('feedback');
      // Focus textarea after render
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleFeedbackSubmit = () => {
    setStep('revision');
  };

  const handleSkipFeedback = () => {
    setStep('revision');
  };

  const handleRevisionYes = () => {
    onSubmit(feedback.trim() || undefined, true);
  };

  const handleRevisionNo = () => {
    onSubmit(feedback.trim() || undefined, false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      {/* Backdrop click */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {step === 'feedback' ? (
          /* Step 1: Feedback */
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 pr-8">
              {t('iteration.card.feedbackDialog.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('iteration.card.feedbackDialog.description')}
            </p>

            {/* Star display */}
            <div className="mt-3 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <svg
                  key={s}
                  className={`w-5 h-5 ${s <= stars ? 'text-yellow-400' : 'text-gray-300'}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>

            <textarea
              ref={textareaRef}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              className="mt-4 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
              placeholder={t('iteration.card.feedbackDialog.placeholder')}
              maxLength={2000}
            />

            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={handleSkipFeedback}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('iteration.card.feedbackDialog.skip')}
              </button>
              <button
                onClick={handleFeedbackSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                {t('iteration.card.feedbackDialog.submit')}
              </button>
            </div>
          </div>
        ) : (
          /* Step 2: Revision prompt */
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 pr-8">
              {t('iteration.card.feedbackDialog.revisionTitle')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('iteration.card.feedbackDialog.revisionDescription')}
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleRevisionNo}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('iteration.card.feedbackDialog.revisionNo')}
              </button>
              <button
                onClick={handleRevisionYes}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700"
              >
                {t('iteration.card.feedbackDialog.revisionYes')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
